import { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useDeviceTest } from 'hooks/useDeviceTest';
import speakersTestSound from 'assets/sounds/speakers-test.wav';
import { logger } from 'utils/logger';
import { useAudio } from './useAudio';

export type PlayOutcome = 'played' | 'aborted' | 'failed';

interface PlaySoundOptions {
  url?: string;
  onEnd?: () => void;
  onStart?: () => void;
  onError?: () => void;
}

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

export const useSpeakersTest = () => {
  const { audio, resetAudio } = useAudio();
  const [testStatus, setTestStatus] = useDeviceTest();

  const stopTestSound = useCallback(() => {
    resetAudio();
    setTestStatus('idle');
  }, [resetAudio, setTestStatus]);

  const playTestSound = useCallback(
    async (options: PlaySoundOptions = {}): Promise<PlayOutcome> => {
      if (audio === null || ['processing', 'loading'].includes(testStatus)) {
        return 'failed';
      }

      setTestStatus('loading');

      audio.src = options.url || speakersTestSound;

      // A load failure both rejects play() and fires the element error event;
      // report it exactly once.
      let reported = false;
      const reportFailure = (error: unknown, extra: Record<string, unknown>) => {
        if (reported) {
          return;
        }
        reported = true;
        setTestStatus('failure');
        Sentry.captureException(error, { extra });
        options.onError?.();
      };

      audio.onplay = () => {
        setTestStatus('processing');
        options.onStart?.();
      };

      audio.onended = () => {
        setTestStatus('idle');
        options.onEnd?.();
      };

      audio.onerror = () => {
        const mediaErrorCode = audio.error?.code;
        reportFailure(new Error(`Device test sound failed (MediaError code ${mediaErrorCode})`), {
          src: audio.currentSrc || audio.src,
          mediaErrorCode,
        });
      };

      try {
        await audio.play();
      } catch (error) {
        if (isAbortError(error)) {
          // Expected on stop / device switch (resetAudio pauses a pending play).
          setTestStatus('idle');
          return 'aborted';
        }
        reportFailure(error, { src: audio.currentSrc || audio.src });
        return 'failed';
      }

      logger.remote({ action: true }).info('User tested speakers');
      return 'played';
    },
    [audio, testStatus, setTestStatus]
  );

  return { playTestSound, stopTestSound, status: testStatus, setTestStatus };
};
